// frontend/src/context/CartContext.jsx
import React, { createContext, useContext, useState, useEffect } from "react";
import {
  getCart,
  addToCart,
  updateCartItem,
  removeFromCart,
  clearCart,
} from "../api";
import { useAuth } from "./AuthContext";

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

const INITIAL_CART_STATE = {
  items: [],
  totalAmount: { usd: 0, sll: 0, inr: 0 },
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(INITIAL_CART_STATE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  const fetchCart = async () => {
    if (!user) {
      setCart(INITIAL_CART_STATE);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await getCart();
      setCart(data || INITIAL_CART_STATE);
    } catch (error) {
      console.error("Error fetching cart:", error);
      setError(error.response?.data?.message || "Failed to load cart");
      setCart(INITIAL_CART_STATE);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCart();
  }, [user]);

  const addItem = async (productId, quantity) => {
    if (!user) {
      console.warn("Cannot add to cart: User not logged in");
      return false;
    }
    setError(null);
    try {
      const { data } = await addToCart(productId, quantity);
      setCart(data);
      return true;
    } catch (error) {
      console.error("Error adding to cart:", error);
      setError(error.response?.data?.message || "Failed to add item to cart");
      throw error;
    }
  };

  const updateItem = async (itemId, quantity) => {
    if (!user) return;
    setError(null);
    try {
      const { data } = await updateCartItem(itemId, quantity);
      setCart(data);
    } catch (error) {
      console.error("Error updating cart:", error);
      setError(error.response?.data?.message || "Failed to update cart");
    }
  };

  const removeItem = async (itemId) => {
    if (!user) return;
    setError(null);
    try {
      const { data } = await removeFromCart(itemId);
      setCart(data);
    } catch (error) {
      console.error("Error removing from cart:", error);
      setError(
        error.response?.data?.message || "Failed to remove item from cart",
      );
    }
  };

  const emptyCart = async () => {
    if (!user) return;
    setError(null);
    try {
      await clearCart();
      setCart(INITIAL_CART_STATE);
    } catch (error) {
      console.error("Error clearing cart:", error);
      setError(error.response?.data?.message || "Failed to clear cart");
    }
  };

  const getCartCount = () => {
    return cart.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
  };

  const getCartTotal = () => {
    return cart.totalAmount?.usd || 0;
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        loading,
        error,
        addItem,
        updateItem,
        removeItem,
        emptyCart,
        fetchCart,
        getCartCount,
        getCartTotal,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
